import React, { useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import {
    Sparkles, Upload, AlertTriangle, Loader2, ChevronLeft, ChevronRight,
    SlidersHorizontal, Info,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";

// Iter55 — Column definitions. `key` is the Employee field key; `header` is
// the label; `type` drives cell rendering (text / date / dropdown / pay_rate).
const COLUMNS = [
    { key: "worker_type", header: "Worker Type", type: "worker_type", width: 130 },
    { key: "full_name", header: "Full Name", type: "text", width: 200, sticky: true, required: true },
    { key: "phone", header: "Phone", type: "text", width: 140, mono: true },
    { key: "email", header: "Email", type: "text", width: 180 },
    { key: "national_id", header: "National ID", type: "text", width: 140, mono: true },
    { key: "module_employee_id", header: "Employee ID", type: "text", width: 120, mono: true },
    { key: "gender", header: "Gender", type: "gender", width: 100 },
    { key: "date_of_birth", header: "DOB", type: "date", width: 130 },
    { key: "age", header: "Age", type: "computed_age", width: 60, readOnly: true },
    { key: "job_title", header: "Job Title", type: "text", width: 160 },
    { key: "department", header: "Department", type: "text", width: 130 },
    { key: "worksite", header: "Worksite", type: "text", width: 130 },
    { key: "hire_date", header: "Hire Date", type: "date", width: 130 },
    { key: "address", header: "Address", type: "text", width: 160 },
    { key: "pay_rate", header: "Pay Rate", type: "pay_rate", width: 200 },
    { key: "cid_reference", header: "CID Ref.", type: "text", width: 170, mono: true },
    { key: "cid_date_of_issue", header: "CID Issue Date", type: "date", width: 130 },
    { key: "nid_reference", header: "NID Ref.", type: "text", width: 140, mono: true },
    { key: "nid_date_of_issue", header: "NID Issue Date", type: "date", width: 130 },
    { key: "nid_expiry_date", header: "NID Expiry", type: "date", width: 130 },
    { key: "asl_id_card_issue_date", header: "ASL Card Issue", type: "date", width: 130 },
    { key: "asl_id_card_expiry_date", header: "ASL Card Expiry", type: "date", width: 130 },
];

// Ordered list of field-key options the user can pick when overriding a
// header mapping. Must stay in sync with EMPLOYEE_FIELD_KEYS in ai_service.py.
const FIELD_OPTIONS = [
    "full_name", "phone", "email", "national_id", "module_employee_id",
    "gender", "date_of_birth", "job_title", "worker_type", "worker_subtype",
    "employment_type", "department", "worksite", "hire_date", "address",
    "pay_rate", "cid_reference", "cid_date_of_issue",
    "nid_reference", "nid_date_of_issue", "nid_expiry_date",
    "asl_id_card_issue_date", "asl_id_card_expiry_date",
];

const PAGE_SIZE = 25;

function ConfPill({ value, testid }) {
    const pct = Math.round((value || 0) * 100);
    let cls = "bg-rose-100 text-rose-800";
    if (pct >= 80) cls = "bg-emerald-100 text-emerald-800";
    else if (pct >= 60) cls = "bg-amber-100 text-amber-800";
    return <span data-testid={testid} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cls}`}>{pct}%</span>;
}

function computeAgeFromDob(dob) {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d)) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
}

export default function StaffExtractDrawer({ open, onOpenChange, moduleId, onCreated }) {
    const fileRef = useRef(null);
    const { user } = useAuth();
    // Iter59.p7 Part 2 — Admin + HR + Senior + Dept-Mgr see cross-module
    // duplicate signals in the extraction preview. Viewers (finance / read-
    // only) don't need this information — they see clean rows.
    const canSeeCrossDup = [
        ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_SENIOR, ROLES.ASL_DEPT_MGR,
    ].includes(user?.role);
    const [file, setFile] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [result, setResult] = useState(null);
    const [overrides, setOverrides] = useState({});
    const [selected, setSelected] = useState({});
    const [force, setForce] = useState({});
    const [creating, setCreating] = useState(false);
    const [page, setPage] = useState(1);
    const [visibleCols, setVisibleCols] = useState(() => Object.fromEntries(COLUMNS.map((c) => [c.key, true])));
    const [headerMapOverride, setHeaderMapOverride] = useState({});
    // Iter56c — retain skipped-row detail from the most recent approve so the
    // user can open a modal with the exact rows the server refused and why.
    const [skipDetail, setSkipDetail] = useState(null); // { errors, summary } | null

    const reset = () => {
        setFile(null); setResult(null); setOverrides({}); setSelected({});
        setForce({}); setPage(1); setHeaderMapOverride({});
        if (fileRef.current) fileRef.current.value = "";
    };

    const onExtract = async () => {
        if (!file) { toast.error("Pick a file first"); return; }
        setExtracting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (moduleId) fd.append("module_id", moduleId);
            const { data } = await api.post("/ai/staff/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            const sel = {};
            for (const e of data.employees || []) {
                sel[e.tentative_id] = (e.overall_confidence >= 0.7) && !e.duplicate_warning;
            }
            setSelected(sel);
            setPage(1);
            setHeaderMapOverride(data.header_map || {});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setExtracting(false); }
    };

    const rows = result?.employees || [];
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const pageRows = useMemo(
        () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [rows, page],
    );

    const setOverride = (tid, field, value) => {
        setOverrides((o) => ({ ...o, [tid]: { ...(o[tid] || {}), [field]: value } }));
    };

    const getField = (e, key) => {
        const ovr = overrides[e.tentative_id] || {};
        if (ovr[key] !== undefined) return ovr[key];
        const f = e.fields || {};
        // worker_type is a derived alias — read employment_type.
        if (key === "worker_type") return f.employment_type;
        return f[key];
    };

    const getPayRate = (e) => {
        const ovr = overrides[e.tentative_id] || {};
        const base = e.fields?.pay_rate || null;
        const amt = ovr.pay_rate_amount !== undefined ? ovr.pay_rate_amount : base?.amount;
        const freq = ovr.pay_rate_frequency !== undefined ? ovr.pay_rate_frequency : (base?.frequency || "day");
        const conf = base?.confidence != null ? base.confidence : (amt ? 0.9 : 0);
        return { amount: amt ?? "", frequency: freq || "day", confidence: conf,
                 defaulted: base?.frequency_source === "default_assumption" };
    };

    const selectedCount = Object.values(selected).filter(Boolean).length;
    const selectedIds = () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    const setAllSelected = (v) => {
        const next = {};
        for (const r of rows) next[r.tentative_id] = !!v && !r.duplicate_warning;
        setSelected(next);
    };

    const onApprove = async () => {
        if (!result) return;
        const approved = selectedIds();
        if (approved.length === 0) { toast.error("Select at least one row"); return; }
        const forceIds = Object.entries(force).filter(([, v]) => v).map(([k]) => k);
        setCreating(true);
        try {
            const overridesToSend = {};
            for (const tid of approved) {
                const ov = { ...(overrides[tid] || {}) };
                const emp = rows.find((r) => r.tentative_id === tid);
                if (emp) {
                    const pr = getPayRate(emp);
                    const amt = Number(pr.amount);
                    if (amt > 0 && pr.frequency) {
                        ov.pay_rate = { amount: amt, frequency: pr.frequency, confidence: pr.confidence };
                    } else {
                        ov.pay_rate = null;
                    }
                    // worker_type is our alias — send as employment_type.
                    if (ov.worker_type !== undefined) {
                        ov.employment_type = ov.worker_type;
                        delete ov.worker_type;
                    }
                }
                delete ov.pay_rate_amount;
                delete ov.pay_rate_frequency;
                if (Object.keys(ov).length > 0) overridesToSend[tid] = ov;
            }
            const { data } = await api.post(`/ai/staff/${result.interaction_id}/approve`, {
                approved_tentative_ids: approved,
                overrides: overridesToSend,
                force_duplicates: forceIds,
            });
            const ok = data.created?.length || 0;
            const errs = data.errors || [];
            const prs = data.pay_rate_summary || { created: 0, auto_approved: 0, pending: 0 };
            if (ok) {
                let msg = `Created ${ok} employee${ok === 1 ? "" : "s"}`;
                if (prs.created > 0) msg += `, ${prs.created} wage rate${prs.created === 1 ? "" : "s"} auto-created`;
                if (errs.length) msg += `, ${errs.length} row${errs.length === 1 ? "" : "s"} skipped`;
                msg += ".";
                // Iter56c — if any rows were skipped, offer a click-through to
                // the skip-detail modal. `toast.success` accepts an `action`
                // (sonner) for the CTA.
                if (errs.length > 0) {
                    setSkipDetail({ errors: errs, summary: data.skipped_summary || {} });
                    toast.success(msg, {
                        duration: 8000,
                        action: {
                            label: `View skipped (${errs.length})`,
                            onClick: () => setSkipDetail({ errors: errs, summary: data.skipped_summary || {} }),
                        },
                    });
                } else {
                    toast.success(msg);
                }
                onCreated && onCreated(data.created);
                // Keep the drawer open when we have a skip modal to show;
                // otherwise close as before.
                if (errs.length === 0) {
                    onOpenChange(false);
                    reset();
                }
            } else {
                setSkipDetail({ errors: errs, summary: data.skipped_summary || {} });
                toast.error(`No employees created${errs.length ? ` · ${errs.length} skipped` : ""}`, {
                    action: errs.length > 0 ? {
                        label: `View skipped (${errs.length})`,
                        onClick: () => setSkipDetail({ errors: errs, summary: data.skipped_summary || {} }),
                    } : undefined,
                });
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCreating(false); }
    };

    const onReject = async () => {
        if (!result) { onOpenChange(false); return; }
        try { await api.post(`/ai/staff/${result.interaction_id}/reject`, { note: "user rejected" }); } catch (_) { /* fire-and-forget */ }
        onOpenChange(false); reset();
    };

    // Row-level warnings surfaced by the backend.
    const warningsByRow = useMemo(() => {
        const map = {};
        for (const e of rows) {
            if (e.row_warnings && e.row_warnings.length) map[e.tentative_id] = e.row_warnings;
        }
        return map;
    }, [rows]);

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <SheetContent
                className="w-full sm:max-w-[95vw] xl:max-w-[1400px] overflow-y-auto"
                data-testid="staff-extract-drawer"
            >
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 asl-crimson-text" />
                        AI: Extract employees from spreadsheet or document
                    </SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-4">
                    {!result && (
                        <div className="border-2 border-dashed rounded-lg p-6 text-center"
                             style={{ borderColor: "var(--asl-border)" }}>
                            <Upload className="h-8 w-8 mx-auto text-[color:var(--asl-muted)]" />
                            <p className="text-sm mt-2 text-[color:var(--asl-muted)]">
                                Drop an .xlsx / .csv / PDF / JPG / PNG (≤ 10 MB)
                            </p>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".xlsx,.xls,.csv,.pdf,image/jpeg,image/png,image/webp"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="block mx-auto mt-3"
                                data-testid="extract-file-input"
                            />
                            {file && <div className="text-xs mt-2 mono">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
                            <Button onClick={onExtract} disabled={!file || extracting}
                                    className="mt-3 asl-navy-bg text-white" data-testid="extract-run-btn">
                                {extracting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                                {extracting ? "Reading document… (5-15s)" : "Extract"}
                            </Button>
                        </div>
                    )}

                    {result && (
                        <div className="space-y-3">
                            <ExtractSummaryBar result={result} />

                            {rows.length === 0 && (
                                <div className="text-sm text-[color:var(--asl-muted)] bg-amber-50 p-3 rounded"
                                     data-testid="extract-empty-reason">
                                    {result.reason || "No employee data detected."}
                                </div>
                            )}

                            {rows.length > 0 && (
                                <>
                                    <HeaderMapPreview
                                        headerMap={headerMapOverride}
                                        unmappedHeaders={result.unmapped_headers || []}
                                        onChange={(h, v) => setHeaderMapOverride((m) => ({ ...m, [h]: v }))}
                                    />

                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Checkbox
                                            checked={selectedCount > 0 && selectedCount === rows.filter((r) => !r.duplicate_warning).length}
                                            onCheckedChange={(v) => setAllSelected(v)}
                                            data-testid="extract-select-all"
                                        />
                                        <span className="text-xs text-[color:var(--asl-muted)]">
                                            {selectedCount} of {rows.length} selected
                                        </span>
                                        <div className="flex-1" />
                                        <ColumnToggle visibleCols={visibleCols} setVisibleCols={setVisibleCols} />
                                    </div>

                                    <TooltipProvider>
                                        <div className="asl-card overflow-x-auto" data-testid="extract-results-container">
                                            <table className="text-xs border-collapse" data-testid="extract-results-table">
                                                <thead className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                                       style={{ background: "var(--asl-navy-50)" }}>
                                                    <tr>
                                                        <th className="px-2 py-2 sticky left-0 z-20" style={{ background: "var(--asl-navy-50)" }}></th>
                                                        {COLUMNS.filter((c) => visibleCols[c.key]).map((c) => (
                                                            <th key={c.key}
                                                                data-testid={`extract-th-${c.key}`}
                                                                className={`px-2 py-2 text-left ${c.sticky ? "sticky left-8 z-10" : ""}`}
                                                                style={{ minWidth: c.width, background: c.sticky ? "var(--asl-navy-50)" : undefined }}>
                                                                {c.header}
                                                                {c.required && <span className="text-rose-600">*</span>}
                                                            </th>
                                                        ))}
                                                        <th className="px-2 py-2 text-center" data-testid="extract-th-conf">Conf.</th>
                                                        <th className="px-2 py-2 text-center" data-testid="extract-th-warn">!</th>
                                                        <th className="px-2 py-2 text-left" data-testid="extract-th-dup">Dup?</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {pageRows.map((e) => (
                                                        <ExtractRow
                                                            key={e.tentative_id}
                                                            e={e}
                                                            visibleCols={visibleCols}
                                                            getField={getField}
                                                            setOverride={setOverride}
                                                            getPayRate={getPayRate}
                                                            selected={selected}
                                                            setSelected={setSelected}
                                                            force={force}
                                                            setForce={setForce}
                                                            rowWarnings={warningsByRow[e.tentative_id] || []}
                                                            canSeeCrossDup={canSeeCrossDup}
                                                        />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </TooltipProvider>

                                    <PaginationBar page={page} totalPages={totalPages}
                                                   onChange={setPage} totalRows={rows.length} />
                                </>
                            )}

                            <div className="flex items-center justify-end gap-2 sticky bottom-0 py-2"
                                 style={{ background: "var(--asl-bg)" }}>
                                <Button variant="ghost" onClick={onReject} data-testid="extract-reject">
                                    Reject extraction
                                </Button>
                                {rows.length > 0 && (
                                    <Button className="asl-navy-bg text-white" onClick={onApprove}
                                            disabled={creating} data-testid="extract-approve">
                                        {creating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                                        Approve & Create {selectedCount} employee{selectedCount === 1 ? "" : "s"}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </SheetContent>
            <SkipDetailModal
                data={skipDetail}
                onClose={() => setSkipDetail(null)}
            />
        </Sheet>
    );
}

function ExtractSummaryBar({ result }) {
    const kv = [
        ["Rows extracted", result.rows_extracted ?? (result.employees?.length ?? 0), "extract-summary-extracted"],
        ["Rows read", result.rows_read ?? "—", "extract-summary-read"],
        ["Skipped (noise)", result.rows_skipped_as_noise ?? 0, "extract-summary-skipped"],
        ["Sheets used", (result.sheets_used || []).join(", ") || "—", "extract-summary-sheets"],
        ["Warnings", (result.warnings || []).length, "extract-summary-warnings"],
    ];
    return (
        <div className="asl-card p-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs"
             data-testid="extract-summary-bar">
            {kv.map(([label, value, tid]) => (
                <div key={label}>
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
                    <div className="font-bold asl-navy-text" data-testid={tid}>{value}</div>
                </div>
            ))}
        </div>
    );
}

function HeaderMapPreview({ headerMap, unmappedHeaders, onChange }) {
    const entries = Object.entries(headerMap || {});
    if (entries.length === 0 && unmappedHeaders.length === 0) return null;
    return (
        <details className="asl-card p-3" data-testid="extract-header-map">
            <summary className="text-xs font-bold cursor-pointer asl-navy-text flex items-center gap-2">
                <Info className="h-3.5 w-3.5" />
                Column mapping ({entries.length} mapped, {unmappedHeaders.length} unmapped)
                <span className="text-[10px] font-normal text-[color:var(--asl-muted)]">
                    — click to expand and override
                </span>
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                {entries.map(([excel, field]) => (
                    <div key={excel} className="flex items-center gap-2 text-xs"
                         data-testid={`extract-header-map-row-${field}`}>
                        <span className="mono text-[color:var(--asl-muted)]">{excel}</span>
                        <span className="text-[color:var(--asl-muted)]">→</span>
                        <select
                            className="h-7 text-xs border rounded px-1"
                            value={field}
                            onChange={(ev) => onChange(excel, ev.target.value)}
                            data-testid={`extract-header-map-select-${excel.replace(/\W+/g, "_")}`}
                        >
                            {FIELD_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                            <option value="unknown">unknown</option>
                        </select>
                    </div>
                ))}
                {unmappedHeaders.map((h) => (
                    <div key={h} className="flex items-center gap-2 text-xs">
                        <span className="mono text-rose-600">{h}</span>
                        <span className="text-[color:var(--asl-muted)]">→</span>
                        <select
                            className="h-7 text-xs border rounded px-1"
                            defaultValue="unknown"
                            onChange={(ev) => onChange(h, ev.target.value)}
                        >
                            <option value="unknown">unknown</option>
                            {FIELD_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                    </div>
                ))}
            </div>
        </details>
    );
}

function ColumnToggle({ visibleCols, setVisibleCols }) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="extract-col-toggle">
                    <SlidersHorizontal className="h-3 w-3 mr-1" /> Show columns
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 max-h-96 overflow-y-auto" align="end">
                <div className="space-y-1">
                    {COLUMNS.map((c) => (
                        <label key={c.key} className="flex items-center gap-2 text-xs cursor-pointer">
                            <Checkbox
                                checked={!!visibleCols[c.key]}
                                onCheckedChange={(v) => setVisibleCols((s) => ({ ...s, [c.key]: !!v }))}
                                data-testid={`extract-col-toggle-${c.key}`}
                            />
                            <span>{c.header}</span>
                        </label>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function PaginationBar({ page, totalPages, onChange, totalRows }) {
    return (
        <div className="flex items-center justify-between text-xs" data-testid="extract-pagination">
            <div className="text-[color:var(--asl-muted)]">
                Page {page} of {totalPages} · {totalRows} rows
            </div>
            <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 text-xs px-2"
                        disabled={page <= 1}
                        onClick={() => onChange(page - 1)}
                        data-testid="extract-page-prev">
                    <ChevronLeft className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs px-2"
                        disabled={page >= totalPages}
                        onClick={() => onChange(page + 1)}
                        data-testid="extract-page-next">
                    <ChevronRight className="h-3 w-3" />
                </Button>
            </div>
        </div>
    );
}

function ExtractRow({ e, visibleCols, getField, setOverride, getPayRate,
                      selected, setSelected, force, setForce, rowWarnings, canSeeCrossDup }) {
    const dup = e.duplicate_warning;
    const crossDup = e.cross_module_duplicate_notice;
    return (
        <tr className="border-t" style={{ borderColor: "var(--asl-border)" }}
            data-testid={`extract-row-${e.tentative_id}`}>
            <td className="px-2 py-1.5 sticky left-0 z-10 relative" style={{ background: "var(--asl-bg)" }}>
                <Checkbox
                    checked={!!selected[e.tentative_id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [e.tentative_id]: !!v }))}
                    data-testid={`extract-select-${e.tentative_id}`}
                />
                {/* Iter59.p7 Part 2 — Cross-module duplicate NOTICE. Non-blocking.
                    Only rendered for admin + dept-mgr (viewers should not see
                    other-module signals). Hover reveals the matched employee. */}
                {crossDup && canSeeCrossDup && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="absolute -top-0.5 -right-0.5 cursor-help"
                                  data-testid={`extract-xmod-dup-${e.tentative_id}`}>
                                <AlertTriangle className="h-3 w-3 text-amber-500 fill-amber-100" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm text-[11px] leading-relaxed">
                            <div className="font-semibold mb-1">Cross-module match</div>
                            <div>
                                Also exists in <strong>{crossDup.matched_employee.module_name || crossDup.matched_employee.module_code}</strong>{" "}
                                as <span className="mono">{crossDup.matched_employee.employee_code}</span>
                                {crossDup.matched_employee.job_title
                                    ? <> ({crossDup.matched_employee.job_title}{crossDup.matched_employee.worker_category ? ` / ${crossDup.matched_employee.worker_category}` : ""})</>
                                    : null}.
                            </div>
                            <div className="mt-1">
                                <em>This is a different person in a different module — approving will create a separate record.</em>
                            </div>
                            <div className="mt-1 text-[10px] text-[color:var(--asl-muted)]">
                                Matched on: {crossDup.matched_on.join(", ")}
                            </div>
                        </TooltipContent>
                    </Tooltip>
                )}
            </td>
            {COLUMNS.filter((c) => visibleCols[c.key]).map((c) => (
                <ExtractCell key={c.key} c={c} e={e} getField={getField}
                             setOverride={setOverride} getPayRate={getPayRate} />
            ))}
            <td className="px-2 py-1.5 text-center">
                <ConfPill value={e.overall_confidence} testid={`extract-conf-${e.tentative_id}`} />
            </td>
            <td className="px-2 py-1.5 text-center">
                {rowWarnings.length > 0 ? (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span data-testid={`extract-warn-${e.tentative_id}`} className="cursor-help">
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-600 inline" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                            <ul className="text-[10px] space-y-1">
                                {rowWarnings.map((w, i) => (
                                    <li key={i}>
                                        <strong>{w.field}:</strong> {w.issue}
                                        {w.raw_value ? <span className="mono"> ({w.raw_value})</span> : null}
                                    </li>
                                ))}
                            </ul>
                        </TooltipContent>
                    </Tooltip>
                ) : <span className="text-[10px] text-emerald-700">—</span>}
            </td>
            <td className="px-2 py-1.5">
                {dup ? (
                    <div className="flex items-center gap-1 text-amber-700">
                        <AlertTriangle className="h-3 w-3" />
                        <span className="text-[10px]">{dup.match_field}</span>
                        <label className="flex items-center gap-1 ml-1">
                            <Checkbox
                                checked={!!force[e.tentative_id]}
                                onCheckedChange={(v) => setForce((s) => ({ ...s, [e.tentative_id]: !!v }))}
                                data-testid={`extract-force-${e.tentative_id}`}
                            />
                            <span className="text-[10px]">Add anyway</span>
                        </label>
                    </div>
                ) : <span className="text-[10px] text-emerald-700">—</span>}
            </td>
        </tr>
    );
}

function ExtractCell({ c, e, getField, setOverride, getPayRate }) {
    const value = getField(e, c.key);
    const tid = `extract-${c.key}-${e.tentative_id}`;
    const stickyStyle = c.sticky
        ? { position: "sticky", left: "2rem", background: "var(--asl-bg)", zIndex: 5, minWidth: c.width }
        : { minWidth: c.width };
    if (c.type === "text" || c.type === "date") {
        return (
            <td className="px-2 py-1.5" style={stickyStyle}>
                <Input
                    className={`h-7 text-xs ${c.mono ? "mono" : ""}`}
                    type={c.type === "date" ? "date" : "text"}
                    value={value ?? ""}
                    onChange={(ev) => setOverride(e.tentative_id, c.key, ev.target.value)}
                    data-testid={tid}
                />
            </td>
        );
    }
    if (c.type === "worker_type") {
        return (
            <td className="px-2 py-1.5" style={stickyStyle}>
                <select className="h-7 text-xs border rounded px-1 w-full"
                        value={value || "casual"}
                        onChange={(ev) => setOverride(e.tentative_id, "worker_type", ev.target.value)}
                        data-testid={tid}>
                    <option value="casual">Casual</option>
                    <option value="non_casual">Non-Casual</option>
                </select>
            </td>
        );
    }
    if (c.type === "gender") {
        return (
            <td className="px-2 py-1.5" style={stickyStyle}>
                <select className="h-7 text-xs border rounded px-1 w-full"
                        value={value || ""}
                        onChange={(ev) => setOverride(e.tentative_id, c.key, ev.target.value || null)}
                        data-testid={tid}>
                    <option value="">—</option>
                    <option value="male">M</option>
                    <option value="female">F</option>
                    <option value="other">Other</option>
                </select>
            </td>
        );
    }
    if (c.type === "computed_age") {
        const dob = getField(e, "date_of_birth");
        const age = computeAgeFromDob(dob);
        return (
            <td className="px-2 py-1.5 text-center mono" style={stickyStyle} data-testid={tid}>
                {age ?? "—"}
            </td>
        );
    }
    if (c.type === "pay_rate") {
        const pr = getPayRate(e);
        const conf = pr.confidence || 0;
        let dotCls = "bg-slate-300";
        let dotTitle = "No rate detected";
        if (pr.amount) {
            if (conf >= 0.8) { dotCls = "bg-emerald-500"; dotTitle = `High confidence (${Math.round(conf * 100)}%)`; }
            else if (conf >= 0.5) { dotCls = "bg-amber-500"; dotTitle = pr.defaulted ? "Unit defaulted to /day — verify" : `Verify (${Math.round(conf * 100)}%)`; }
            else { dotCls = "bg-rose-500"; dotTitle = `Low confidence (${Math.round(conf * 100)}%)`; }
        }
        return (
            <td className="px-2 py-1.5" style={stickyStyle} data-testid={`extract-payrate-cell-${e.tentative_id}`}>
                <div className="flex items-center gap-1">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${dotCls}`} />
                        </TooltipTrigger>
                        <TooltipContent>{dotTitle}</TooltipContent>
                    </Tooltip>
                    <Input
                        className="h-7 text-xs mono w-16"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="—"
                        value={pr.amount}
                        onChange={(ev) => setOverride(e.tentative_id, "pay_rate_amount", ev.target.value === "" ? "" : Number(ev.target.value))}
                        data-testid={`extract-payrate-amount-${e.tentative_id}`}
                    />
                    <select
                        className="h-7 text-xs border rounded px-1"
                        value={pr.frequency}
                        onChange={(ev) => setOverride(e.tentative_id, "pay_rate_frequency", ev.target.value)}
                        data-testid={`extract-payrate-freq-${e.tentative_id}`}
                    >
                        <option value="day">/day</option>
                        <option value="hour">/hour</option>
                        <option value="week">/week</option>
                        <option value="biweek">/biweek</option>
                        <option value="month">/month</option>
                    </select>
                </div>
            </td>
        );
    }
    return <td className="px-2 py-1.5" style={stickyStyle}>{value ?? "—"}</td>;
}

/**
 * Iter56c — SkipDetailModal
 * Displays the exact rows the server refused during approve, with a labelled
 * reason per row + an aggregate summary at the top. Lets the user see WHICH
 * of their uploaded rows dropped and WHY, so they can fix the source data
 * and re-upload (or re-approve with force_duplicates set).
 */
function SkipDetailModal({ data, onClose }) {
    const open = !!data;
    const errors = data?.errors || [];
    const summary = data?.summary || {};
    const summaryEntries = Object.entries(summary);
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col"
                data-testid="skip-detail-modal"
            >
                <DialogHeader>
                    <DialogTitle>
                        {errors.length} row{errors.length === 1 ? "" : "s"} were skipped
                    </DialogTitle>
                </DialogHeader>

                {summaryEntries.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2" data-testid="skip-summary-chips">
                        {summaryEntries.map(([reason, count]) => (
                            <span
                                key={reason}
                                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900"
                                data-testid={`skip-chip-${reason.replace(/\W+/g, "_")}`}
                            >
                                {count} × {reason}
                            </span>
                        ))}
                    </div>
                )}

                <div className="overflow-auto flex-1 border border-slate-200 rounded mt-3">
                    <table className="w-full text-xs" data-testid="skip-detail-table">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 sticky top-0">
                            <tr>
                                <th className="px-3 py-2 text-left">Row</th>
                                <th className="px-3 py-2 text-left">Full Name</th>
                                <th className="px-3 py-2 text-left">Employee ID</th>
                                <th className="px-3 py-2 text-left">Phone</th>
                                <th className="px-3 py-2 text-left">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {errors.map((e, i) => (
                                <tr
                                    key={e.tentative_id || i}
                                    className="border-t"
                                    data-testid={`skip-row-${e.tentative_id || i}`}
                                >
                                    <td className="px-3 py-2 mono text-slate-500">
                                        {e.tentative_id || `#${i + 1}`}
                                    </td>
                                    <td className="px-3 py-2 font-medium">
                                        {e.full_name || "—"}
                                    </td>
                                    <td className="px-3 py-2 mono">
                                        {e.module_employee_id || "—"}
                                    </td>
                                    <td className="px-3 py-2 mono">
                                        {e.phone || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-amber-900">
                                        {e.reason || e.error || "unknown"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="skip-detail-close"
                    >
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
